import axios from 'axios';
import { saveAs } from 'src/utils/fileSaver';

export const EXPORT_PATH = '/api/v5/truck_shifts/export';

// truck_ids[] を明示で並べる（axios の配列シリアライズ差異を避け Rails の params[:truck_ids] に確実に届ける）。
export const buildExportQuery = (startOn: string, endOn: string, truckIds: number[]): string => {
  const params = new URLSearchParams();
  params.set('start_on', startOn);
  params.set('end_on', endOn);
  truckIds.forEach((id) => params.append('truck_ids[]', String(id)));
  return params.toString();
};

// Content-Disposition から filename を解決。RFC5987 の filename*=charset'lang'value を
// 優先（両方ある場合・順序に依らず filename* を採る）し、無ければ quoted filename= にフォールバック。
export const resolveFilename = (contentDisposition: string | undefined, fallback: string): string => {
  if (!contentDisposition) return fallback;
  // filename*=UTF-8''... を最優先（charset'lang' プレフィックスは任意長を許容）
  const ext = contentDisposition.match(/filename\*=(?:[^']*'[^']*')?([^;]+)/i);
  if (ext?.[1]) {
    const raw = ext[1].trim().replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  // 次点: filename="..." / filename=...
  const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (plain?.[1]) return plain[1].trim().replace(/"/g, '');
  return fallback;
};

// blob のエラーレスポンスを text 化して JSON errors を抽出（無ければ汎用）。
const extractBlobError = async (error: unknown): Promise<string> => {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const json = JSON.parse(await error.response.data.text()) as { errors?: string[] };
      if (json.errors?.length) return json.errors.join(' / ');
    } catch {
      // not JSON → fallthrough
    }
  }
  return 'エクスポートに失敗しました';
};

export const downloadTruckShiftsExport = async (
  { startOn, endOn, truckIds }: { startOn: string; endOn: string; truckIds: number[] },
): Promise<void> => {
  try {
    const response = await axios.get<Blob>(`${EXPORT_PATH}?${buildExportQuery(startOn, endOn, truckIds)}`, { responseType: 'blob' });
    const filename = resolveFilename(
      response.headers['content-disposition'] as string | undefined,
      `truck_shifts_${startOn}_${endOn}.xlsx`,
    );
    saveAs(response.data, filename);
  } catch (error) {
    throw new Error(await extractBlobError(error));
  }
};
